import { Link as RouterLink } from 'react-router-dom';
// mui
import { styled, alpha } from '@mui/material/styles';
import { Box, Grid, Link, Divider, Container, Typography, IconButton, Stack } from '@mui/material';
// routes
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
//
import Logo from '../../../components/logo';

// data
import {
  CONATCTLIST,
  SUPPORT_EMAIL_CARD,
  SOCIALS,
  LINKS,
  LINK_DOWNLOADS,
  telHref
} from '../../../data/contact-info';
// ----------------------------------------------------------------------

const MUTED = 'rgba(255, 255, 255, 0.6)';
const HAIRLINE = 'rgba(255, 255, 255, 0.12)';

// Legal pages that already exist as routes in the app.
const LEGAL_LINKS = [
  { name: 'Privacy Policy', href: '/privacy' },
  { name: 'Terms & Conditions', href: '/terms' },
  { name: 'Refund Policy', href: '/refund' },
  { name: 'Delete Account', href: '/delete-account' },
];

// Clamp helpers instead of fixed height/width, which clipped longer addresses.
const CLAMP_2 = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

const CLAMP_4 = { ...CLAMP_2, WebkitLineClamp: 4 };

const StyledAddress = styled(Typography)(CLAMP_4);

const RootStyle = styled('div')(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.common.black,
  paddingBottom: theme.spacing(6),
  color: MUTED,
  backgroundImage: `radial-gradient(80% 120% at 0% 0%, ${alpha(theme.palette.primary.main, 0.14)} 0%, transparent 55%), radial-gradient(70% 110% at 100% 0%, ${alpha('#DF3E30', 0.12)} 0%, transparent 55%)`,
}));

// Thin brand accent across the top edge instead of a dashed divider.
const AccentBar = styled('div')({
  height: 4,
  background: 'linear-gradient(90deg, #DF3E30 0%, #1877F2 50%, #1C9CEA 100%)',
});

// ----------------------------------------------------------------------

const columnSx = {
  mb: { xs: 4, md: 0 },
};

const columnTitleSx = {
  color: 'common.white',
  mb: 1.5,
  textTransform: 'uppercase',
  letterSpacing: 1,
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: "''",
    position: 'absolute',
    left: 0,
    bottom: -6,
    width: 24,
    height: 2,
    borderRadius: 1,
    backgroundColor: '#DF3E30',
  },
};

const linkSx = {
  color: MUTED,
  display: 'block',
  py: 0.6,
  width: 'fit-content',
  textDecoration: 'none',
  transition: 'color 0.25s ease, transform 0.25s ease',
  '@media (hover: hover)': {
    '&:hover': { color: '#DF3E30', transform: 'translateX(3px)' },
  },
};

export default function Footer() {
  const year = new Date().getFullYear();

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <RootStyle>
      <AccentBar />

      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 7 } }}>
        <Grid container spacing={{ xs: 4, md: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} sx={columnSx}>
            <MotionInView variants={varFadeInUp}>
              <Logo
                url="/assets/logos/asap.png"
                sx={{ height: { xs: 48, md: 60 }, mx: { xs: 'auto', md: 'inherit' }, mb: 1 }}
              />
              <Typography variant="body2" sx={{ color: MUTED, letterSpacing: 2, textAlign: { xs: 'center', sm: 'left' } }}>
                DREAM | SEEK | ACHIEVE
              </Typography>

              <Stack spacing={1} direction="row" sx={{ mt: 2.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                {SOCIALS.map((social) => (
                  <IconButton
                    key={social.name}
                    component="a"
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    sx={{
                      p: 0.75,
                      color: MUTED,
                      border: `1px solid ${HAIRLINE}`,
                      borderRadius: '50%',
                      transition: 'color 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
                      '@media (hover: hover)': {
                        '&:hover': {
                          color: 'common.white',
                          borderColor: '#DF3E30',
                          transform: 'translateY(-2px)',
                        },
                      },
                    }}
                  >
                    <Iconify icon={social.icon} width={16} />
                  </IconButton>
                ))}
              </Stack>
            </MotionInView>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} sx={columnSx}>
            <MotionInView variants={varFadeInUp} transition={{ delay: 0.08 }}>
              {LINKS.map((list) => {
                const { headline, children } = list;
                return (
                  <Box key={headline}>
                    <Typography component="p" variant="overline" sx={columnTitleSx}>
                      {headline}
                    </Typography>
                    <Box component="nav">
                      {children.map((link) => (
                        <Link
                          to={link.href}
                          key={link.name}
                          component={RouterLink}
                          variant="body2"
                          underline="none"
                          sx={linkSx}
                        >
                          {link.name}
                        </Link>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </MotionInView>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 4, lg: 3 }} sx={columnSx}>
            <MotionInView variants={varFadeInUp} transition={{ delay: 0.16 }}>
              <Typography component="p" variant="overline" sx={columnTitleSx}>
                Get in touch
              </Typography>

              <Stack spacing={{ xs: 2, md: 2.5 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                  <Iconify
                    icon="ic:round-mail-outline"
                    width={16}
                    sx={{ color: '#DF3E30', mt: 0.4, flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ color: 'common.white' }}>
                      {SUPPORT_EMAIL_CARD.label}
                    </Typography>
                    <Link
                      href={SUPPORT_EMAIL_CARD.emailHref}
                      underline="hover"
                      variant="body2"
                      sx={{ color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                    >
                      {SUPPORT_EMAIL_CARD.email}
                    </Link>
                    <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.25 }}>
                      {SUPPORT_EMAIL_CARD.blurb}
                    </Typography>
                  </Box>
                </Stack>

                {CONATCTLIST.map((site) => (
                  <Stack key={site.name} spacing={0.75} sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                      <Iconify icon={site.flag} width={18} />
                      <Typography variant="subtitle2" sx={{ color: 'common.white' }}>
                        {site.name}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                      <Iconify
                        icon="ic:round-call"
                        width={16}
                        sx={{ color: '#DF3E30', mt: 0.4, flexShrink: 0 }}
                      />
                      <Link
                        href={telHref(site.phone)}
                        underline="hover"
                        variant="body2"
                        sx={{ color: MUTED, width: 'fit-content' }}
                      >
                        {site.phone}
                      </Link>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                      <Iconify
                        icon="mdi:address-marker-outline"
                        width={16}
                        sx={{ color: '#DF3E30', mt: 0.4, flexShrink: 0 }}
                      />
                      <StyledAddress variant="body2" sx={{ color: MUTED }}>
                        {site.address}
                      </StyledAddress>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </MotionInView>
          </Grid>

          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 3 }}>
            <MotionInView variants={varFadeInUp} transition={{ delay: 0.24 }}>
              <Typography component="p" variant="overline" sx={columnTitleSx}>
                Download App
              </Typography>

              <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexWrap: 'wrap' }}>
                {LINK_DOWNLOADS.map((link) => (
                  <Box
                    key={link.name}
                    component="a"
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Get the ASAP app on ${link.name}`}
                    sx={{
                      display: 'block',
                      width: { xs: 130, sm: 150 },
                      borderRadius: 1.5,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '@media (hover: hover)': {
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.45)',
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={link.logo}
                      alt={`Download on ${link.name}`}
                      loading="lazy"
                      sx={{ width: '100%', display: 'block' }}
                    />
                  </Box>
                ))}
              </Stack>
            </MotionInView>
          </Grid>
        </Grid>

        <Divider sx={{ my: { xs: 3, md: 4 }, borderColor: HAIRLINE }} />

        <MotionInView variants={varFadeInUp}>
          <Stack
            spacing={{ xs: 2, md: 3 }}
            sx={{ alignItems: 'center', justifyContent: 'center' }}
            sx={{ textAlign: 'center' }}
          >
            <Typography
              component="p"
              variant="body2"
              sx={{ color: MUTED, fontSize: 13 }}
            >
              © 2021-{year} Garliksofts. All rights reserved.
            </Typography>

            <Stack direction="row" useFlexGap spacing={2} sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
              {LEGAL_LINKS.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  component={RouterLink}
                  underline="none"
                  variant="body2"
                  sx={{
                    color: MUTED,
                    fontSize: 13,
                    transition: 'color 0.25s ease',
                    '@media (hover: hover)': { '&:hover': { color: 'common.white' } },
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </Stack>
          </Stack>
        </MotionInView>

        <Box
          component="span"
          aria-hidden
          sx={{
            position: 'fixed',
            left: '50%',
            bottom: { xs: 24, md: 32 },
            transform: 'translateX(-50%)',
            zIndex: (theme) => theme.zIndex.tooltip + 1,
            pointerEvents: 'none',
          }}
        >
          <IconButton
            onClick={handleBackToTop}
            aria-label="Back to top"
            sx={{
              color: MUTED,
              border: `1px solid ${HAIRLINE}`,
              width: 48,
              height: 48,
              flexShrink: 0,
              pointerEvents: 'auto',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              backgroundColor: alpha('#000', 0.25),
              transition: 'color 0.25s ease, border-color 0.25s ease, transform 0.25s ease, background-color 0.25s ease, box-shadow 0.25s ease',
              '@media (hover: hover)': {
                '&:hover': { color: 'common.white', borderColor: '#DF3E30', transform: 'translateY(-4px)' },
              },
            }}
          >
            <Iconify icon="eva:arrow-upward-outline" width={22} />
          </IconButton>
        </Box>
      </Container>
    </RootStyle>
  );
}
